import { computed, onBeforeUnmount, onMounted, ref, toValue } from "vue";
const isMac = /Mac|iPhone|iPad/.test(navigator.userAgent);
// A screenshot on the clipboard has no name of its own — the browser supplies a generic one
// ("image.png"), so consecutive pastes would be indistinguishable in a caller's file list.
// Stamp those; a file copied out of the OS file manager keeps its real name.
const GENERIC_CLIPBOARD_NAME = /^(image|screenshot)\.[a-z0-9]+$/i;
// Take the extension from the file's own name, else from its MIME subtype ("image/svg+xml" →
// ".svg"). A file we can read neither from keeps no extension — the upload path routes on MIME
// type, and inventing one would misdescribe the bytes.
const extensionFor = (file) => {
    const fromName = /\.([a-z0-9]+)$/i.exec(file.name || "")?.[1];
    const subtype = (file.type || "").split(";")[0].split("/")[1]?.split("+")[0];
    const extension = fromName || subtype;
    return extension ? `.${extension.toLowerCase()}` : "";
};
const nameForPastedFile = (file, index) => {
    if (file.name && !GENERIC_CLIPBOARD_NAME.test(file.name))
        return file;
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    const suffix = index > 0 ? `-${index + 1}` : "";
    return new File([file], `pasted-${stamp}${suffix}${extensionFor(file)}`, { type: file.type });
};
// Chrome/Firefox fill clipboardData.files on paste; some browsers only fill items.
const clipboardFiles = (clipboard) => {
    const files = Array.from(clipboard.files);
    if (files.length > 0)
        return files;
    return Array.from(clipboard.items)
        .filter((item) => item.kind === "file")
        .map((item) => item.getAsFile())
        .filter((file) => file !== null);
};
// A paste aimed at a field the user is typing in belongs to that field, unless the clipboard
// holds nothing that field could take.
const isEditableTarget = (target) => target instanceof HTMLElement && (target.isContentEditable || ["INPUT", "TEXTAREA"].includes(target.tagName));
// Accepts the same forms as an <input accept> attribute — extensions (".pdf"), exact MIME types
// ("application/pdf") and wildcards ("image/*") — as a comma-separated string or an array, so a
// caller can pass the very string it gives its file input.
const parseAccept = (accept) => {
    const rules = Array.isArray(accept) ? accept : String(accept || "").split(",");
    return rules.map((rule) => rule.trim().toLowerCase()).filter(Boolean);
};
const matchesAccept = (file, rules) => {
    const name = (file.name || "").toLowerCase();
    const type = (file.type || "").toLowerCase();
    return rules.some((rule) => {
        if (rule.startsWith("."))
            return name.endsWith(rule);
        if (rule.endsWith("/*"))
            return type.startsWith(rule.slice(0, -1));
        return type === rule;
    });
};
// Content hash, so a jumpy Ctrl+V doesn't upload the same screenshot twice. Only byte-identical
// pastes match — re-snipping the same region of the screen produces different bytes, and that is
// a genuinely new file as far as we can tell. Needs a secure context (https, or localhost in dev);
// where crypto.subtle is missing we skip deduplication rather than guess from size and type, since
// wrongly dropping a file the user pasted is worse than uploading a duplicate.
const digestFor = async (file) => {
    if (!crypto?.subtle)
        return null;
    const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
    return Array.from(new Uint8Array(digest))
        .map((byte) => byte.toString(16).padStart(2, "0"))
        .join("");
};
// ".pdf, image/png" → "PDF, PNG", for the rejection message.
const describeAccept = (rules) => {
    const labels = rules.map((rule) => (rule.startsWith(".") ? rule.slice(1) : rule.split("/").pop().replace("*", "any")).toUpperCase());
    return [...new Set(labels)].join(", ");
};
export function usePastedFiles({ onFiles, enabled = true, multiple = true, accept = "", }) {
    const pasteHint = computed(() => `or press ${isMac ? "⌘V" : "Ctrl+V"} to paste a screenshot or copied file`);
    // Paste-specific problems only — a paste the caller never saw. Failures inside onFiles stay the
    // caller's to report, since it knows which file failed and why.
    const pasteAlert = ref(null);
    const clearPasteAlert = () => {
        pasteAlert.value = null;
    };
    const raise = (type, text) => {
        pasteAlert.value = { type, text };
    };
    // Content hashes of every paste handed over so far. Per component instance: cross-session
    // deduplication would need the hash stored with the file server-side.
    const handledDigests = new Set();
    // The name each of those hashes was handed over under, so a caller that lets the user delete one
    // uploaded file can retire exactly that file's hash. Keyed on the name rather than handing digests
    // out to the caller, which would make every caller keep this bookkeeping. Two pasted files that
    // end up with the same name share an entry — the hash check still stops identical bytes being
    // re-uploaded, and it only costs the ability to forget them individually.
    const digestsByName = new Map();
    const forgetPaste = (fileName) => {
        const digest = digestsByName.get(fileName);
        if (digest)
            handledDigests.delete(digest);
        digestsByName.delete(fileName);
    };
    const forgetAll = (files) => files.forEach((file) => forgetPaste(file.name));
    // Listens on the document, so the user can paste without first clicking into the upload area.
    // Two enabled instances on one page would therefore upload the same paste twice — keep it
    // enabled on a single uploader per page.
    const handlePaste = async (event) => {
        if (!toValue(enabled))
            return;
        const clipboard = event.clipboardData;
        if (!clipboard)
            return;
        let files = clipboardFiles(clipboard);
        if (files.length === 0)
            return;
        // Let the focused field have the paste when the clipboard holds something it can take.
        if (isEditableTarget(event.target) && clipboard.getData("text/plain"))
            return;
        event.preventDefault();
        clearPasteAlert();
        if (!toValue(multiple))
            files = files.slice(0, 1);
        const rules = parseAccept(toValue(accept));
        if (rules.length > 0) {
            const accepted = files.filter((file) => matchesAccept(file, rules));
            const skipped = files.length - accepted.length;
            if (accepted.length === 0) {
                // Claimed the paste and dropped it, so say so — silence reads as the paste not working.
                raise("error", `That paste isn't a file type you can upload here. Supported types: ${describeAccept(rules)}.`);
                return;
            }
            if (skipped > 0) {
                raise("warning", `Skipped ${skipped} pasted ${skipped === 1 ? "file" : "files"} of an unsupported type. ` +
                    `Supported types: ${describeAccept(rules)}.`);
            }
            files = accepted;
        }
        // Drop anything already pasted this session, and repeats within this paste itself. The digests
        // are awaited once, and everything from there to the claim below is synchronous — so two
        // overlapping pastes cannot both get through: whichever resumes first claims its hashes before
        // the other's filter runs, and the other then sees them.
        const digests = await Promise.all(files.map(digestFor));
        const claimed = new Set();
        const fresh = files
            .map((file, index) => ({ file, digest: digests[index] }))
            .filter(({ digest }) => {
            if (digest && (handledDigests.has(digest) || claimed.has(digest)))
                return false;
            if (digest)
                claimed.add(digest);
            return true;
        });
        const repeats = files.length - fresh.length;
        if (fresh.length === 0) {
            raise("warning", `You already pasted ${files.length === 1 ? "that file" : "those files"}.`);
            return;
        }
        if (repeats > 0) {
            raise("warning", `Skipped ${repeats} ${repeats === 1 ? "file" : "files"} you had already pasted.`);
        }
        claimed.forEach((digest) => handledDigests.add(digest));
        // Name them now and record which hash went out under which name, so the caller can retire one
        // of them on its own later.
        const named = fresh.map(({ file }, index) => nameForPastedFile(file, index));
        named.forEach((file, index) => {
            const digest = fresh[index].digest;
            if (digest)
                digestsByName.set(file.name, digest);
        });
        try {
            // false means the caller couldn't take the files, so forget them and let the same paste be
            // retried. A partial failure rolls back the whole paste — re-pasting then re-uploads the
            // files that did land, which is what a user repeating the paste is asking for anyway.
            if ((await onFiles(named)) === false)
                forgetAll(named);
        }
        catch {
            // onFiles rejecting outright (rather than reporting per-file failures) would otherwise
            // leave the paste looking like it did nothing.
            forgetAll(named);
            raise("error", `Couldn't upload the pasted ${named.length === 1 ? "file" : "files"}. Please try again.`);
        }
    };
    onMounted(() => document.addEventListener("paste", handlePaste));
    onBeforeUnmount(() => document.removeEventListener("paste", handlePaste));
    return { pasteHint, pasteAlert, clearPasteAlert, forgetPaste };
}
